import { ViewEncapsulation } from '@angular/core';
import { Component, Input } from '@angular/core';
import { EventResource } from '@apaq/orgkit-js';
import { addDays, addWeeks, differenceInDays, endOfDay, format, isBefore, isFuture, isPast } from 'date-fns';

import { LinkService } from 'src/app/services/link.service';
import { environment } from 'src/environments/environment';
import { container } from 'tsyringe';
import { AccountingSettings, SettingsResource } from '@apaq/orgkit-js';
import { DialogService } from '../dialog/dialog.service';
import { Data, SettingsApp } from '../settings/settings.app';
import { AuthenticationService, ContextService, Roles } from 'ngx-orgkit';

interface IEvent {
  data: any;
  type: string;
}

@Component({
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom
})
export class TopBarComponent {
  
  events = new Map<string, IEvent>();
  loading?: boolean;
  loadingStart?: Date;
  supportUrl?: string = environment.externalUrls.support;
  helpUrl?: string = environment.externalUrls.help;
  _authenticated = false;
  checkLoadingTimer?: any;

  constructor(private context: ContextService, private auth: AuthenticationService,
    private linkService: LinkService, private dialogService: DialogService,
    private settingsResource: SettingsResource,
    private eventResource: EventResource) {
    auth.authenticationChange.subscribe(async _ => {
      this._authenticated = await this.auth.isAuthenticated();
    });
    this.auth.isAuthenticated().then(value => this._authenticated = value);

    this.eventResource.stream().on('planning.started', (data: any) => {
      const id = this.getRouteOptimizeId(data.data);
      const event = { type: 'planning.started', data: data };
      this.addEvent(id, event);
    });

    this.eventResource.stream().on('planning.completed', (data) => {
      const id = this.getRouteOptimizeId(data.data);
      this.removeEvent(id);
    });

    this.eventResource.stream().on('planning.failed', (data) => {
      const id = this.getRouteOptimizeId(data.data);
      if (isBefore(data.from, addWeeks(new Date(), 4))) {
        // Only show error if it regards the coming 4 weeks. Otherwise ignore the error.
        this.showError(data.errors);
      }
      this.removeEvent(id);
    });

    setInterval(() => this.checkLoading(), 10000);

    this.context.contextChange.subscribe(_ => {
      this.checkAccountingConnection();
      this.checkExpiration();
    });
    this.checkAccountingConnection();
    this.checkExpiration();

  }

  get authenticated() {
    return this._authenticated;
  }

  get showManagement() {
    const roles = this.context.account?.roles ?? [];
    return roles.includes(Roles.ADMIN_ROLE) || 
      Roles.hasPermissionsForOrganization(roles, this.context.organizationId!, Roles.ORGROLE_MANAGEMENT) ||
      Roles.hasPermissionsForOrganization(roles, this.context.organizationId!, Roles.ORGROLE_ADMIN);
  }

  get initials() {
    if (this.context?.account) {
      const name = this.context?.account.name;
      const names = name.split(' ');
      return names[0].charAt(0).toLocaleUpperCase() + (names.length > 1 ? names[names.length - 1].charAt(0).toLocaleUpperCase() : '');
    } else {
      return '?'
    }
  }

  handleLogout() {
    this.auth.signOut();
    this.linkService.openSignIn();
  }

  handleShowSettings() {
    this.dialogService.open(SettingsApp, '', { headerStyle: 'discreet', width: '750px', padding: 'none' });
  }

  handleShowProfile() {
    //this.linkService.openProfile();
    const data: Data = {area: 'profile'};
    this.dialogService.open(SettingsApp, '', { headerStyle: 'discreet', width: '750px', padding: 'none' , data});
  }

  handleShowHelp() {
    window.open(this.helpUrl, "_blank");
  }

  handleContactSupport() {
    window.open(this.supportUrl, "_blank");
  }

  private getRouteOptimizeId(data: { from: Date, to: Date }) {
    const from = data.from;
    const to = data.to;
    return format(from, 'yyyy-MM-dd') + '_' + format(to, 'yyyy-MM-dd');
  }

  async checkAccountingConnection() {
    if (this.context.account != null && this.context.organizationId != null) {
      const settings = await this.settingsResource.findById('accounting') as AccountingSettings;
      if (settings.system != 'None' && settings.connectionFailed) {
        Stallion.toast($localize`Cannot connect to accounting system. Open settings to adjust connection with accounting system.`, 'danger');
      }
    }
  }

  checkExpiration() {
    if (this.context.account == null || this.context.organization == null) {
      return;
    }
    
    let message = '';
    let messageType: 'warning' | 'danger';
    let messageTime: number | undefined = 6000;

    if(this.isTrialAboutToExpire()) {
      message = $localize`Your trial for ${this.context.organization.name} is about to expire.`;
      messageType = 'warning';
    } else if(this.isAboutToExpire()) {
      message = $localize`Your subscription for ${this.context.organization.name} is about to expire.`;
      messageType = 'warning';
    } else if(this.isOverDue()) {
      message = $localize`Your subscription for ${this.context.organization.name} is over due.`;
      messageType = 'warning';
    } else if(this.isExpired()) {
      message = $localize`Your subscription for ${this.context.organization.name} has expired.`;
      messageType = 'danger';
      messageTime = undefined;
    } else {
      return;
    }

    message += ' ';
    message += $localize`Setup your payment in settings if you want to keep your access.`;
    Stallion.toast(message, messageType, undefined, messageTime);
  }

  public isOverDue() {
    return this.context.organization?.subscriptionOverDue;
  }

  public isAboutToExpire(trial = false) {
    if (this.isExpired()) {
      return false;
    }
    const now = new Date();

    return this.context.organization &&
      this.context.organization.subscriptionPlan !== null &&
      isFuture(endOfDay(this.context.organization.subscriptionExpires)) &&
      Math.abs(differenceInDays(this.context.organization.subscriptionExpires, now)) < 8 &&
      this.context.organization.subscriptionCardNumber == null &&
      this.context.organization.trial === trial;

  }

  public isExpired() {
    if (this.context.organization) {
      let expireDate = this.context.organization.subscriptionExpires;
      expireDate = addDays(expireDate, 3); // Add leeway
      var expired = isPast(expireDate);
      return expired || this.context.organization.terminated;
    } else {
      return false;
    }
  }

  public get expireDate() {
    if (this.context.organization) {
      return this.context.organization.subscriptionExpires;
    } else {
      return null;
    }

  }

  public isTrialAboutToExpire() {
    return this.isAboutToExpire(true);
  }

  private addEvent(id: string, event: IEvent) {
    if (this.loadingStart == null) {
      this.loadingStart = new Date();
    }
    this.events.set(id, event);
    this.loading = true;
    clearTimeout(this.checkLoadingTimer);
  }

  private removeEvent(id: string) {
    this.events.delete(id);
    clearTimeout(this.checkLoadingTimer);
    this.checkLoadingTimer = setTimeout(() => this.checkLoading(), 2000);
  }

  private checkLoading() {
    if (this.loadingStart != null) {
      const millisSinceStart = new Date().getTime() - this.loadingStart.getTime();
      this.loading = Object.keys(this.events).length > 0 && millisSinceStart < 60000;

      if (!this.loading) {
        this.loadingStart = undefined;

      }
    }
  }

  private showError(errors: string[]) {
    const msg = $localize`Some routes could not be optimized` + ':\n\n' + errors.join('\n\n');
    Stallion.toast(msg, 'warning');
  }

}